import { createStore, createHook, createActionsHook } from 'react-sweet-state';

import type { MarkdownConvertDirection } from '@atlassian/editor-plugin-markdown-mode/markdownModePluginType';

/**
 * Shared open/close state for the markdown-mode conversion modals.
 *
 * Two flavours live here:
 *  - `dialog` — the unified rich-text ↔ markdown confirmation dialog.
 *    `toMarkdown` may carry `isLossy: true` to trigger the warning
 *    treatment; `toRichText` is never lossy from the FE's POV.
 *  - `syncBlockCount` (> 0 ⇔ open) — the informational "Unlink synced
 *    blocks to convert" blocker shown when the doc contains any
 *    `bodiedSyncBlock`. No "proceed" action — the user must unlink in
 *    the editor first.
 *
 * Both flavours are mutually exclusive: opening one clears the other.
 *
 * State is lifted out of `useMarkdownModeActions` so the three entry
 * points (header overflow menu, title `.md`-suffix gesture, `cmd+shift+x`
 * shortcut) drive the same dialog instances, which are rendered exactly
 * once (currently in `MoreActions`). Single global flag is fine because
 * only one Live page editor is mounted at a time. If that ever changes,
 * key by `contentId`.
 */
type ConvertDialogClosed = { isOpen: false };
type ConvertDialogOpen = {
	isOpen: true;
	direction: MarkdownConvertDirection;
	isLossy: boolean;
};
export type ConvertDialogStateValue = ConvertDialogClosed | ConvertDialogOpen;

type ConvertDialogState = {
	dialog: ConvertDialogStateValue;
	/**
	 * Number of `bodiedSyncBlock` nodes from the most recent convert
	 * attempt. `> 0` ⇔ the unlink-synced-blocks blocker is open.
	 */
	syncBlockCount: number;
};

const initialState: ConvertDialogState = {
	dialog: { isOpen: false },
	syncBlockCount: 0,
};

export type OpenConvertDialogPayload = {
	direction: MarkdownConvertDirection;
	/** Only meaningful for `toMarkdown`. Ignored for `toRichText` (forced to `false`). */
	isLossy?: boolean;
};

export const ConvertDialogStore = createStore({
	initialState,
	actions: {
		openConvertDialog:
			(payload: OpenConvertDialogPayload) =>
			({ setState, getState }) => {
				const next: ConvertDialogStateValue = {
					isOpen: true,
					direction: payload.direction,
					isLossy: payload.direction === 'toMarkdown' ? Boolean(payload.isLossy) : false,
				};
				const current = getState();
				if (
					current.dialog.isOpen &&
					current.dialog.direction === next.direction &&
					current.dialog.isLossy === next.isLossy &&
					current.syncBlockCount === 0
				) {
					return;
				}
				setState({ dialog: next, syncBlockCount: 0 });
			},
		closeConvertDialog:
			() =>
			({ setState, getState }) => {
				if (getState().dialog.isOpen) {
					setState({ dialog: { isOpen: false } });
				}
			},
		openSyncBlockBlocker:
			(count: number) =>
			({ setState }) => {
				setState({ syncBlockCount: count, dialog: { isOpen: false } });
			},
		closeSyncBlockBlocker:
			() =>
			({ setState, getState }) => {
				if (getState().syncBlockCount > 0) {
					setState({ syncBlockCount: 0 });
				}
			},
	},
	name: 'ConvertDialogStore',
});

export const useConvertDialogState = createHook(ConvertDialogStore);
export const useConvertDialogActions = createActionsHook(ConvertDialogStore);
